import { useState } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import { Button } from "../../../ui/button";

export const GameModal = ({ game, league, teams, divisions, onSave, onClose, saving }) => {
  const [mode, setMode] = useState("single"); // single or quick-add
  const [formData, setFormData] = useState({
    home_team_id: game?.home_team_id || "",
    away_team_id: game?.away_team_id || "",
    divisions: game?.divisions || [],
    scheduled_date: game?.scheduled_date || "",
    scheduled_time: game?.scheduled_time || "",
    location: game?.location || "",
    is_conference_game: game?.is_conference_game || false,
    is_division_game: game?.is_division_game || false,
    youtube_url: game?.youtube_url || "",
    tv_network: game?.tv_network || "",
    notes: game?.notes || ""
  });
  
  const [quickGames, setQuickGames] = useState([
    { home_team_id: "", away_team_id: "", scheduled_date: "", scheduled_time: "" }
  ]);

  const toggleDivision = (divId) => {
    const current = formData.divisions || [];
    if (current.includes(divId)) {
      setFormData({ ...formData, divisions: current.filter(d => d !== divId) });
    } else {
      setFormData({ ...formData, divisions: [...current, divId] });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleQuickAdd = async () => {
    const validGames = quickGames.filter(g => g.home_team_id && g.away_team_id && g.scheduled_date);
    for (const g of validGames) {
      await onSave({
        ...g,
        is_conference_game: formData.is_conference_game,
        is_division_game: formData.is_division_game,
        divisions: formData.divisions,
        location: formData.location
      });
    }
  };

  const addQuickGame = () => {
    setQuickGames([...quickGames, { home_team_id: "", away_team_id: "", scheduled_date: quickGames[quickGames.length - 1]?.scheduled_date || "", scheduled_time: "" }]);
  };

  const updateQuickGame = (idx, field, value) => {
    const newGames = [...quickGames];
    newGames[idx][field] = value;
    setQuickGames(newGames);
  };

  const removeQuickGame = (idx) => {
    if (quickGames.length > 1) {
      setQuickGames(quickGames.filter((_, i) => i !== idx));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-bold text-white">
              {game ? "Edit Game" : "Add Games"}
            </h3>
            {!game && (
              <div className="flex bg-zinc-800 rounded p-0.5">
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  className={`px-3 py-1 text-xs rounded ${mode === "single" ? "bg-orange-500 text-white" : "text-zinc-400"}`}
                >
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => setMode("quick-add")}
                  className={`px-3 py-1 text-xs rounded ${mode === "quick-add" ? "bg-orange-500 text-white" : "text-zinc-400"}`}
                >
                  Quick Add
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mode === "single" ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Away Team *</label>
                  <select
                    value={formData.away_team_id}
                    onChange={(e) => setFormData({ ...formData, away_team_id: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    required
                  >
                    <option value="">Select Team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Home Team *</label>
                  <select
                    value={formData.home_team_id}
                    onChange={(e) => setFormData({ ...formData, home_team_id: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    required
                  >
                    <option value="">Select Team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Time</label>
                  <input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    placeholder="Venue"
                  />
                </div>
              </div>

              {divisions.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-2">Divisions (select all that apply)</label>
                  <div className="flex flex-wrap gap-2">
                    {divisions.map(div => (
                      <button
                        key={div.id}
                        type="button"
                        onClick={() => toggleDivision(div.id)}
                        className={`px-3 py-1 text-xs rounded border transition-colors ${
                          formData.divisions?.includes(div.id)
                            ? "bg-blue-500/20 border-blue-500 text-blue-400"
                            : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                        }`}
                      >
                        {div.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={formData.is_conference_game}
                    onChange={(e) => setFormData({ ...formData, is_conference_game: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-zinc-300">Conference</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={formData.is_division_game}
                    onChange={(e) => setFormData({ ...formData, is_division_game: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-zinc-300">Division</span>
                </label>
              </div>

              <details className="border-t border-zinc-800 pt-4">
                <summary className="text-xs font-medium text-zinc-500 cursor-pointer">Broadcast Info (Optional)</summary>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <input
                    type="text"
                    value={formData.tv_network}
                    onChange={(e) => setFormData({ ...formData, tv_network: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    placeholder="TV Network"
                  />
                  <input
                    type="url"
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                    placeholder="Stream URL"
                  />
                </div>
              </details>

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={onClose} className="text-sm">Cancel</Button>
                <Button
                  type="submit"
                  disabled={!formData.home_team_id || !formData.away_team_id || !formData.scheduled_date || saving}
                  className="bg-orange-500 hover:bg-orange-600 text-sm"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {game ? "Update" : "Add Game"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-zinc-800/50 rounded-lg p-3 space-y-3">
                <p className="text-xs text-zinc-500">Settings applied to all games:</p>
                <div className="flex flex-wrap gap-4">
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-sm w-40"
                    placeholder="Location"
                  />
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={formData.is_conference_game}
                      onChange={(e) => setFormData({ ...formData, is_conference_game: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-zinc-300">Conference</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={formData.is_division_game}
                      onChange={(e) => setFormData({ ...formData, is_division_game: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-zinc-300">Division</span>
                  </label>
                </div>
                {divisions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {divisions.map(div => (
                      <button
                        key={div.id}
                        type="button"
                        onClick={() => toggleDivision(div.id)}
                        className={`px-2 py-0.5 text-xs rounded border ${
                          formData.divisions?.includes(div.id)
                            ? "bg-blue-500/20 border-blue-500 text-blue-400"
                            : "bg-zinc-800 border-zinc-700 text-zinc-400"
                        }`}
                      >
                        {div.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs text-zinc-500 px-1">
                  <div className="col-span-3">Away</div>
                  <div className="col-span-3">Home</div>
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Time</div>
                  <div className="col-span-1"></div>
                </div>
                {quickGames.map((qg, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <select
                      value={qg.away_team_id}
                      onChange={(e) => updateQuickGame(idx, 'away_team_id', e.target.value)}
                      className="col-span-3 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-xs"
                    >
                      <option value="">Away</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name?.substring(0, 15)}</option>)}
                    </select>
                    <select
                      value={qg.home_team_id}
                      onChange={(e) => updateQuickGame(idx, 'home_team_id', e.target.value)}
                      className="col-span-3 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-xs"
                    >
                      <option value="">Home</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name?.substring(0, 15)}</option>)}
                    </select>
                    <input
                      type="date"
                      value={qg.scheduled_date}
                      onChange={(e) => updateQuickGame(idx, 'scheduled_date', e.target.value)}
                      className="col-span-3 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-xs"
                    />
                    <input
                      type="time"
                      value={qg.scheduled_time}
                      onChange={(e) => updateQuickGame(idx, 'scheduled_time', e.target.value)}
                      className="col-span-2 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => removeQuickGame(idx)}
                      className="col-span-1 p-1 text-zinc-500 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addQuickGame}
                className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add another game
              </button>

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={onClose} className="text-sm">Cancel</Button>
                <Button
                  onClick={handleQuickAdd}
                  disabled={quickGames.filter(g => g.home_team_id && g.away_team_id && g.scheduled_date).length === 0 || saving}
                  className="bg-orange-500 hover:bg-orange-600 text-sm"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Add {quickGames.filter(g => g.home_team_id && g.away_team_id && g.scheduled_date).length} Games
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
